<script setup lang="ts">
defineProps<{
    variant?:
        | "primary"
        | "ghost"
        | "danger"
        | "danger-solid"
        | "pill"
        | "pill-ghost";
    size?: "sm" | "md";
    disabled?: boolean;
    // Идёт операция: вместо содержимого — три прыгающие точки, клики выключены.
    loading?: boolean;
}>();

// Событие пробрасывается наружу, чтобы у родителя работали модификаторы
// (@click.stop и т.п.).
const emit = defineEmits<{ click: [e: MouseEvent] }>();
</script>

<template>
    <button
        class="mc-btn"
        :class="[
            `mc-btn_${variant ?? 'primary'}`,
            `mc-btn_${size ?? 'md'}`,
            { 'mc-btn_loading': loading },
        ]"
        :disabled="disabled || loading"
        @click="emit('click', $event)"
    >
        <span v-if="loading" class="mc-btn__dots" aria-label="Загрузка">
            <span class="mc-btn__dot" />
            <span class="mc-btn__dot" />
            <span class="mc-btn__dot" />
        </span>
        <slot v-else />
    </button>
</template>

<style lang="scss" scoped>
.mc-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 6px;
    font-weight: 700;
    min-height: 46px;
    transition: 0.1s;
    white-space: nowrap;
    text-transform: uppercase;

    &:disabled {
        opacity: 0.4;
        pointer-events: none;
    }

    // Loading — не «выключенная», а занятая кнопка: непрозрачная, с точками.
    &_loading:disabled {
        opacity: 1;
    }

    &__dots {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    &__dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: currentColor;
        animation: mc-btn-dot 0.9s ease-in-out infinite;

        &:nth-child(2) {
            animation-delay: 0.15s;
        }

        &:nth-child(3) {
            animation-delay: 0.3s;
        }
    }

    &_md {
        height: 32px;
        padding: 0 14px;
        font-size: 13px;
    }

    &_sm {
        height: 26px;
        padding: 0 10px;
        font-size: 12px;
    }

    &_primary {
        background: var(--mc-acid);
        color: #000;

        &:hover {
            box-shadow: 0 0 34px var(--mc-acid-glow);
        }
    }

    &_ghost {
        color: var(--mc-fg-mute);

        &:hover {
            color: var(--mc-fg);
            background: var(--mc-bg-sel);
        }
    }

    &_danger {
        color: var(--mc-fg-mute);
        border: 1px solid var(--mc-line-hard);

        &:hover {
            color: var(--mc-danger);
            border-color: var(--mc-danger);
        }
    }

    // Пилюли для кнопок поверх медиа (запись видео и т.п.): компактные,
    // по контенту (без width:100%/min-height обычных кнопок), моно-шрифт.
    &_pill,
    &_pill-ghost {
        width: auto;
        min-height: 0;
        height: 33px;
        padding: 0 18px;
        font-family: var(--mc-mono);
        font-size: 12px;
        letter-spacing: 0.06em;
    }

    // Acid-пилюля с постоянным свечением.
    &_pill {
        background: var(--mc-acid);
        color: #0a0c0a;
        box-shadow: 0 0 20px var(--mc-acid-glow);
        transition: box-shadow 0.15s;

        &:hover {
            box-shadow: 0 0 32px var(--mc-acid-glow-strong);
        }
    }

    // Тёмная полупрозрачная — вторичное действие поверх медиа.
    &_pill-ghost {
        background: rgba(0, 0, 0, 0.6);
        color: #fff;

        &:hover {
            background: rgba(0, 0, 0, 0.8);
        }
    }

    // По умолчанию как обычный danger (приглушённый контур), на ховере заливается
    // красным.
    &_danger-solid {
        color: var(--mc-danger);
        border: 1px solid var(--mc-danger);

        &:hover {
            background: var(--mc-danger);
            color: var(--mc-bg-sel);
        }
    }
}

@keyframes mc-btn-dot {
    0%,
    60%,
    100% {
        transform: translateY(0);
    }
    30% {
        transform: translateY(-4px);
    }
}
</style>
